// Dependencies
import { useCallback, useEffect, useMemo, useRef, useState, Dispatch, ChangeEvent, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { NodeChange, EdgeChange, Connection } from '@xyflow/react';
import * as _ from 'underscore';
import { v4 as uuid } from 'uuid';

// Types and Interfaces
import { RealtimeClient } from '../../services/realtime-client';
import { IGlobalState, GlobalAction, GlobalActionTypeList } from '../../states/GlobalState';
import { initialGlobalState } from '../../states/GlobalStateReducer';

// Types
import {
  UbiverseEventTopicList,
  UbiverseClient,
  UbiverseNodeType__Child
} from '../../types';

// Styles
import './index.scss';

// Components
import Ubiverse from '../../components/Ubiverse';
import UbiverseControl from '../../components/UbiverseControl';
import EditorPanel from '../../components/EditorPanel';
import NavigationBar, { NavigationSection } from '../../components/NavigationBar';
import AuthDialog from '../../components/AuthDialog';
import { TimelineViewerNodePreview, SimulatorNodePreview, ButtonNodePreview } from './NodePreviews';
import { useAuth } from '../../auth/AuthProvider';
import { getSupabaseClient } from '../../services/supabase';
import { listDevices } from '../../services/devices';
import { loadLatestProjectForUser, loadProjectById, ProjectRecord, saveProjectForUser } from '../../services/projects';

// Interfaces
interface DashboardProps {
  realtimeClient: RealtimeClient;
  state: IGlobalState;
  dispatch: Dispatch<GlobalAction>;
}

interface SavedProject {
  id: string;
  name: string;
  updatedAt: string;
}

interface UserProfile {
  id: string;
  display_name: string | null;
  created_at: string;
}

const recentProjectsKey = (userId: string | null | undefined) =>
  userId ? `ubiquitel.recentProjects.${userId}` : null;

const createDefaultProjectName = () => `project_${new Date().toISOString().replace(/[.:]/g, '-')}`;

// Dashboard component
const Dashboard = ({ realtimeClient, state, dispatch }: DashboardProps) => {
  const navigate = useNavigate();
  const [sessionList, setSessionList] = useState<UbiverseClient[]>([]);
  const { signOut, user, isAuthenticated } = useAuth();
  const [stateCache, setStateCache] = useState<IGlobalState | null>(null);
  const [recentProjects, setRecentProjects] = useState<SavedProject[]>([]);
  const [isNodePanelCollapsed, setIsNodePanelCollapsed] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [currentProject, setCurrentProject] = useState<ProjectRecord | null>(null);
  const [isProjectBootstrapped, setIsProjectBootstrapped] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

  const draggableNodes = [
    {
      type: 'timelineViewerNode',
      label: 'Timeline Sequence',
      description: 'A piano-roll sequence lane for composing vibration timing, intensity, and playback structure.',
      preview: <TimelineViewerNodePreview />,
    },
    {
      type: 'simulatorNode',
      label: 'Device',
      description: 'Connect to a live ESP32 finger or browser simulator on the network and route vibration data to it.',
      preview: <SimulatorNodePreview />,
    },
    {
      type: 'buttonNode',
      label: 'Event Button',
      description: 'A manual trigger node for testing socket events with custom JSON payloads in real time.',
      preview: <ButtonNodePreview />,
    }
  ];

  // devices-table names keyed by hardware_id. The firmware doesn't know its
  // own display name (it lives only in the DB), so presence entries of
  // kind:'device' are enriched with it here — the spec's "devices table
  // enriched with current presence" (docs/device-auth.md).
  const deviceNamesRef = useRef<Record<string, string>>({});

  const enrichWithDeviceName = useCallback((client: UbiverseClient): UbiverseClient => {
    if (client.kind !== 'device' || !client.hardware_id || client.data?.label) return client;
    const name = deviceNamesRef.current[client.hardware_id];
    return name ? { ...client, data: { ...client.data, label: name } } : client;
  }, []);

  const handleNodeListChanged = useCallback((payload: UbiverseClient[]) => {
    setSessionList(prev => {
      const children = payload.filter(p => p.type === UbiverseNodeType__Child);
      return children.map(p => {
        const existing = prev.find(n => n.id === p.id);
        return enrichWithDeviceName(existing ? { ...existing, ...p } : p);
      });
    });
  }, [enrichWithDeviceName]);

  useEffect(() => {
    if (!isAuthenticated) return;
    listDevices()
      .then(rows => {
        deviceNamesRef.current = Object.fromEntries(rows.map(r => [r.hardware_id, r.name]));
        setSessionList(prev => prev.map(enrichWithDeviceName));
      })
      .catch(() => { /* enrichment only — roster still renders hardware_ids */ });
  }, [isAuthenticated, enrichWithDeviceName]);

  // This handler gets captured into node data by the injection effects below,
  // so the closure it was built with can be many renders old. Resolve against a
  // ref instead of the captured state.nodes — otherwise a node added since the
  // capture is not found and the selection is silently dropped.
  const nodesRef = useRef(state.nodes);
  useEffect(() => { nodesRef.current = state.nodes; }, [state.nodes]);

  const onSelectSimulatorClient = (nodeId: string, clientId: string | null) => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (!node) return;
    dispatch({
      type: GlobalActionTypeList.UpdateNodeById,
      payload: {
        ...node,
        data: { ...node.data, selectedClientId: clientId }
      }
    });
  };

  const attachDataToNodesFromState = (sourceState: IGlobalState) => {
    return {
      ...sourceState,
      nodes: sourceState.nodes.map(n => ({ ...n, data: { ...n.data, state: sourceState, dispatch, realtimeClient } }))
    }
  }

  const removeDataFromNodesFromState = (sourceState: IGlobalState) => {
    return {
      ...sourceState,
      nodes: sourceState.nodes.map(n => ({ ...n, data: { ...n.data, state: null, dispatch: null, realtimeClient: null } }))
    }
  }

  const getRecentProjects = (userId: string | null | undefined): SavedProject[] => {
    const key = recentProjectsKey(userId);
    if (!key) return [];
    try {
      const serialized = localStorage.getItem(key);
      if (!serialized) return [];
      const parsed = JSON.parse(serialized) as SavedProject[];
      if (!Array.isArray(parsed)) return [];
      return parsed.slice(0, 5);
    } catch {
      return [];
    }
  }

  const persistRecentProject = (project: SavedProject) => {
    const key = recentProjectsKey(user?.id);
    if (!key) return;
    const nextProjects = [project, ...getRecentProjects(user?.id).filter(p => p.id !== project.id)].slice(0, 5);
    localStorage.setItem(key, JSON.stringify(nextProjects));
    setRecentProjects(nextProjects);
  }

  const applyProjectState = (projectState: IGlobalState) => {
    const hydratedState = attachDataToNodesFromState(projectState);
    dispatch({ type: GlobalActionTypeList.SetAll, payload: hydratedState })
    setStateCache(hydratedState);
  }


  const onNodesChange = useCallback((changes: NodeChange[]) => {
    dispatch({ type: GlobalActionTypeList.UpdateNodes, payload: changes })
  }, []);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    dispatch({ type: GlobalActionTypeList.UpdateEdges, payload: changes })
  }, []);

  const onConnect = useCallback((connection: Connection) => {
    dispatch({ type: GlobalActionTypeList.AddEdge, payload: connection })
  }, []);

  // Initiale Nodes
  useEffect(() => {
    realtimeClient.subscribeEvent(UbiverseEventTopicList.SessionListChanged, handleNodeListChanged)
  }, [])

  useEffect(() => {
    if (!isProjectBootstrapped || state.sequences.length > 0) return;

    dispatch({
      type: GlobalActionTypeList.AddSequence,
      payload: {
        id: `sequence--${new Date().getTime()}`,
        name: 'Untitled timeline',
        isMuted: false,
        notes: []
      }
    })
  }, [isProjectBootstrapped, state.sequences.length])

  useEffect(() => {
    // Add Node if there is a new sequence added
    state.sequences.forEach((s, i) => {
      if (!state.nodes.find(n => n.id === s.id)) {
        dispatch({
          type: GlobalActionTypeList.AddNode,
          payload: {
            id: s.id,
            type: 'timelineViewerNode',
            position: { x: 300 + i * 100, y: 300 + i * 100 },
            data: { id: s.id, label: s.name || s.id, state, dispatch, realtimeClient },
            deletable: true,
            connectable: true
          }
        })
      }
    })

    // Remove Node if there is a sequence removed
    state.nodes.filter(n => n.type === 'timelineViewerNode').forEach(n => {
      if (!state.sequences.find(s => s.id === n.id)) {
        dispatch({ type: GlobalActionTypeList.RemoveNode, payload: n.id })
      }
    })
  }, [state.sequences])

  // Keep SimulatorNode data current — on a presence change AND on newly added
  // nodes. A node just dropped on the canvas has neither sessionList nor
  // onSelectClient, and previously only an unrelated state.control/editor/
  // sequences change re-injected them, which is why a fresh Device node only
  // became selectable after pressing Stop.
  // The second filter is what stops this from looping: once injected the
  // sessionList reference matches and onSelectClient exists, so nothing
  // dispatches and the effect settles.
  useEffect(() => {
    state.nodes
      .filter(n => n.type === 'simulatorNode')
      .filter(n => n.data.sessionList !== sessionList || !n.data.onSelectClient)
      .forEach(n => {
        dispatch({
          type: GlobalActionTypeList.UpdateNodeById,
          payload: {
            ...n,
            data: { ...n.data, sessionList, onSelectClient: onSelectSimulatorClient }
          }
        })
      })
  }, [sessionList, state.nodes])

  // State Updates
  useEffect(() => {
    state.nodes.forEach(n => {
      const baseData = {
        ...n.data,
        state,
        dispatch,
        realtimeClient
      };
      // Inject sessionList into SimulatorNodes
      if (n.type === 'simulatorNode') {
        Object.assign(baseData, { sessionList, onSelectClient: onSelectSimulatorClient });
      }
      dispatch({
        type: GlobalActionTypeList.UpdateNodeById,
        payload: { ...n, data: baseData }
      })
    })
  }, [state.control, state.editor, state.sequences])

  const loadUserMetadata = async () => {
    setUserName(null);
    setCurrentProject(null);
    setRecentProjects(getRecentProjects(user?.id));

    if (!user) {
      applyProjectState(initialGlobalState);
      setIsProjectBootstrapped(true);
      return;
    }

    const supabase = getSupabaseClient();
    if (!supabase) {
      setIsProjectBootstrapped(true);
      return;
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('id, display_name, created_at')
      .eq('id', user.id)
      .maybeSingle<UserProfile>();

    if (error) {
      console.error('Failed to load user profile', error.message);
      return;
    }

    setUserName(data?.display_name || null);

    const loadedProject = await loadLatestProjectForUser(user.id);
    if (loadedProject) {
      applyProjectState(loadedProject.state);
      setCurrentProject(loadedProject.project);
      persistRecentProject(loadedProject.project);
    } else {
      applyProjectState(initialGlobalState);
      setStateCache(initialGlobalState);
    }

    setIsProjectBootstrapped(true);
  }

  useEffect(() => {
    loadUserMetadata();
  }, [user?.id]);

  const saveUserMetadata = async () => {
    if (!user) return;

    const nextProjectName = (currentProject?.name || createDefaultProjectName()).trim();
    if (!nextProjectName) {
      window.alert('Project name is required.');
      return;
    }
    const supabase = getSupabaseClient();
    if (!supabase) return;

    const { error } = await supabase
      .from('profiles')
      .upsert({ id: user.id, display_name: userName }, { onConflict: 'id' });

    if (!error) {
      const strippedState = removeDataFromNodesFromState(state);
      const savedProject = await saveProjectForUser({
        userId: user.id,
        projectId: currentProject?.id || uuid(),
        projectName: nextProjectName,
        state: strippedState
      });

      if (savedProject) {
        persistRecentProject(savedProject);
        setCurrentProject(savedProject);
      }

      console.log('User metadata saved successfully');
      setStateCache(state)
    } else {
      console.error('Failed to save user metadata', error.message);
    }
  }

  const saveProjectAs = async () => {
    if (!user) return;

    const defaultName = createDefaultProjectName();
    const projectName = window.prompt('Save project as', defaultName);

    if (projectName === null) return;

    const trimmedName = projectName.trim();
    if (!trimmedName) {
      window.alert('Project name is required.');
      return;
    }

    const strippedState = removeDataFromNodesFromState(state);
    const savedProject = await saveProjectForUser({
      userId: user.id,
      projectId: uuid(),
      projectName: trimmedName,
      state: strippedState
    });

    if (!savedProject) {
      window.alert('Failed to save project as a new copy.');
      return;
    }

    persistRecentProject(savedProject);
    setCurrentProject(savedProject);
    setStateCache(state);
  }

  const updateDisplayName = async () => {
    if (!user) return;

    const nextName = window.prompt('Enter display name', userName || '');
    if (nextName === null) return;

    const supabase = getSupabaseClient();
    if (!supabase) return;

    const { error } = await supabase
      .from('profiles')
      .upsert({ id: user.id, display_name: nextName || null }, { onConflict: 'id' });

    if (error) {
      console.error('Failed to update display name', error.message);
      return;
    }

    setUserName(nextName || null);
  };

  const requestLogin = () => {
    setIsAuthDialogOpen(true);
  };

  const isStateChanged = () => {
    return !_.isEqual(state, stateCache)
  }


  const editProjectNameFromNavbar = () => {
    if (!currentProject) {
      window.alert('Create or open a project first.');
      return;
    }

    const projectName = window.prompt('Edit project name', currentProject.name);

    if (projectName === null) return;

    const trimmedName = projectName.trim();
    if (!trimmedName) {
      window.alert('Project name is required.');
      return;
    }

    const updatedProject: SavedProject = {
      ...currentProject,
      name: trimmedName,
      updatedAt: new Date().toISOString()
    };

    setCurrentProject(updatedProject);
    persistRecentProject(updatedProject);
  }

  const createProject = () => {
    const defaultName = createDefaultProjectName();
    const projectName = window.prompt('Create project', defaultName);

    if (projectName === null) return;

    const trimmedName = projectName.trim();
    if (!trimmedName) {
      window.alert('Project name is required.');
      return;
    }

    setCurrentProject({
      id: uuid(),
      name: trimmedName,
      updatedAt: new Date().toISOString()
    });
    applyProjectState(initialGlobalState);
  }

  const closeProject = () => {
    setCurrentProject(null);
    applyProjectState(initialGlobalState);
  }

  const duplicateProject = () => {
    const strippedState = removeDataFromNodesFromState(state);
    const duplicatedProject: SavedProject = {
      id: uuid(),
      name: createDefaultProjectName(),
      updatedAt: new Date().toISOString()
    };
    persistRecentProject(duplicatedProject);
    setCurrentProject(duplicatedProject);
    applyProjectState(strippedState);
  }

  const openProjectFilePicker = () => {
    fileInputRef.current?.click();
  }

  const openProjectFromFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = JSON.parse(text) as SavedProject & { state?: IGlobalState } | IGlobalState;
    const projectState = 'state' in parsed && parsed.state ? parsed.state : parsed as IGlobalState;
    applyProjectState(projectState);
    const openedProject: SavedProject = {
      id: 'id' in parsed && parsed.id ? parsed.id : uuid(),
      name: 'name' in parsed ? parsed.name : file.name,
      updatedAt: new Date().toISOString()
    };
    persistRecentProject(openedProject);
    setCurrentProject(openedProject);
    event.target.value = '';
  }

  const openRecentProject = async (project: SavedProject) => {
    const loadedProject = await loadProjectById(project.id);

    if (loadedProject) {
      applyProjectState(loadedProject.state);
      setCurrentProject(loadedProject.project);
      return;
    }

    setCurrentProject(project);
    applyProjectState(initialGlobalState);
  }

  const launchSimulator = () => {
    window.open('/sim', '_blank');
  }

  const onNodeDragStart = (event: DragEvent<HTMLDivElement>, type: string) => {
    event.dataTransfer.setData('application/ubiverse-node-type', type);
    event.dataTransfer.effectAllowed = 'move';
  }

  const openPreferences = () => {
    window.alert('Preferences are coming soon.');
  }

  const navigationSections: NavigationSection[] = useMemo(() => {
    return [
      {
        label: 'Projects',
        actions: [
          { label: 'Create project', onClick: createProject },
          { label: 'Open projects', onClick: openProjectFilePicker },
          { label: `Edit project name${currentProject ? `: ${currentProject.name}` : ''}`, onClick: editProjectNameFromNavbar, disabled: !currentProject },
          ...(isAuthenticated ? recentProjects.map((project) => ({
            label: `Open recent: ${project.name}${project.id === currentProject?.id ? ' (active)' : ''}`,
            onClick: () => openRecentProject(project)
          })) : []),
          {
            label: `Save project${currentProject ? `: ${currentProject.name}` : ''}`,
            onClick: isAuthenticated ? saveUserMetadata : requestLogin,
            disabled: isAuthenticated && !isStateChanged()
          },
          {
            label: 'Save project as',
            onClick: isAuthenticated ? saveProjectAs : requestLogin
          },
          { label: 'Duplicate project', onClick: duplicateProject },
          { label: 'Close project', onClick: closeProject }
        ]
      },
      {
        label: 'Simulator',
        actions: [{ label: 'Launch simulator', onClick: launchSimulator }]
      },
      {
        label: 'Account',
        actions: [
          { label: 'Preferences', onClick: openPreferences },
          { label: `Display name: ${userName || '(not set)'}`, onClick: updateDisplayName, disabled: !isAuthenticated },
          { label: 'Devices', onClick: () => navigate('/devices'), disabled: !isAuthenticated },
          { label: 'Log In', onClick: requestLogin, disabled: isAuthenticated },
          { label: 'Log out', onClick: signOut, disabled: !isAuthenticated }
        ],
        ...(isAuthenticated ? { avatar: { name: userName || user?.email || undefined } } : {}),
      }
    ]
  }, [recentProjects, state, stateCache, isAuthenticated, userName, currentProject]);

  return (
    <div className="page--dashboard">
      <NavigationBar sections={navigationSections} />
      <AuthDialog
        isOpen={isAuthDialogOpen}
        onClose={() => setIsAuthDialogOpen(false)}
      />
      <input
        ref={fileInputRef}
        style={{ display: 'none' }}
        type="file"
        accept="application/json"
        onChange={openProjectFromFile}
      />
      <div className="page--dashboard__ubiverse-control">
        <UbiverseControl
          state={state}
          dispatch={dispatch}
          realtimeClient={realtimeClient}
        />
      </div>
      <aside className={`page--dashboard__node-panel ${isNodePanelCollapsed ? 'collapsed' : ''}`}>
        <div className="page--dashboard__node-panel__header">
          {!isNodePanelCollapsed && <h6 className="page--dashboard__node-panel__title">Nodes</h6>}
        </div>

        {!isNodePanelCollapsed && (
          <div className="page--dashboard__node-panel__list">
            {draggableNodes.map((node) => (
              <div
                key={node.type}
                className="page--dashboard__node-panel__item"
                draggable={true}
                onDragStart={(event) => onNodeDragStart(event, node.type)}
                title={node.description}
              >
                <div className="page--dashboard__node-panel__item__preview">
                  {node.preview}
                </div>
                <p className="page--dashboard__node-panel__item__title">{node.label}</p>
              </div>
            ))}
          </div>
        )}
      </aside>
      <button
        className="small page--dashboard__node-panel-toggle"
        onClick={() => setIsNodePanelCollapsed(!isNodePanelCollapsed)}
      >
        {isNodePanelCollapsed ? '»' : '«'}
      </button>
      <div className="page--dashboard__container">
        <Ubiverse
          state={state}
          dispatch={dispatch}
          realtimeClient={realtimeClient}
          sessionList={sessionList}
          onSelectSimulatorClient={onSelectSimulatorClient}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
        />
      </div>
      <div className={`page--dashboard__editor-panel__container ${state.editor.targetId ? 'active' : ''}`}>
        <EditorPanel state={state} dispatch={dispatch} realtimeClient={realtimeClient} />
      </div>
    </div>
  )
}

export default Dashboard;
